/**
 * Services table (DNKADM-0079; single grouped table DNKADM-0089) — the `v2_service`
 * row rendering extracted from {@link ServicesScreen} so the SAME row renders in both
 * the flat list and the grouped-by-category view (re-house, don't duplicate; identical
 * behavior).
 *
 * ONE source of truth for a service row's cells: {@link ServiceRow} returns the
 * `<TableRow>` (the 9 shared columns) and {@link ServicesTableHead} the matching
 * `<TableHead>`. The flat fallback ({@link ServicesTable}) wraps the head + rows in its
 * own `<Table>`; the grouped view (DNKADM-0089) renders the head ONCE at the top of a
 * single table and emits the rows directly beneath each category's grouping row, so all
 * columns stay equal width across every category and the header never repeats.
 *
 * A row is name (link → detail; a star "Featured" glyph precedes the name on an
 * explicit `featured === true` — never on absent, DNKADM-0290) → credit bucket → staff/resource modes → taxable →
 * #variations → the lifecycle `status` chip (Draft/Active/Inactive, DNKADM-0140 — this
 * replaced the old isActive chip), with Copy (always) + a status-gated Publish (DRAFT)
 * / Deactivate (ACTIVE) / Reactivate (INACTIVE) action, then the trailing open
 * chevron. Opening the record is the row/name/chevron click → the detail route
 * (DNKADM-0284, the MembersList pattern); base-field editing lives on the detail page
 * (DNKADM-0286 retired the list quick-edit trigger). Every action button stops
 * propagation so it never triggers the row open. All data and callbacks are supplied
 * by the owning screen — this piece holds no state and fires no requests beyond the
 * row-open navigation (the grouped view stays a single list call, no per-row fan-out).
 *
 * **Row content (DNKADM-0493).** Name, the status chip (Draft / Turned off / Active, meaning on
 * hover/tap), Lengths and prices, Members pay with (the credit bucket), Who (how the coach and
 * the bay are picked — the modes the row carries) and Staff approval (yes/no). A row says
 * why nobody can book it only from data the list already has; the three reasons the spec
 * names (no coach, no bay, no location) all need each service's assignment reads, which the
 * list does not make, so none is shown here (the service page shows them on its cards).
 *
 * **Sortable head (DNKADM-0430 P1-F).** Given `sort` + `onSort`, the head renders the shared
 * {@link SortableHeader} on every data column (the screen owns the state, in the URL);
 * without them it stays plain.
 */
import { Link as RouterLink, useNavigate } from 'react-router-dom';
import {
  Box,
  Link as MuiLink,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
} from '@mui/material';
import { useTheme } from '@mui/material/styles';
import { IconAction } from '@/components/IconAction';
import { Icon, resolveTokens } from '@/theme';
import { formatMoney } from '@/utils/v2Format';
import type { V2Service } from '@/api/services';
import { SortableHeader } from '@/components/list/SortableHeader';
import type { SortState } from '@/components/list/listSort';
import { serviceName, type ServiceSortKey } from './servicesListView';
import { whoSummary } from './serviceLabels';
import { ServiceStatusChip } from './ServiceStatusChip';

/**
 * The number of columns a service row (and the shared head) spans. The grouped view's
 * full-width category grouping rows use this as their `colSpan` so they span the table.
 */
export const SERVICE_COLUMN_COUNT = 8;

/** The head's sort wiring (DNKADM-0430); omit it for a plain head. */
export interface ServicesTableSortProps {
  sort?: SortState<ServiceSortKey> | undefined;
  onSort?: ((key: ServiceSortKey) => void) | undefined;
}

/** The shared header row (SERVICE_COLUMN_COUNT columns). Rendered ONCE per table (flat or grouped). */
export function ServicesTableHead({ sort, onSort }: ServicesTableSortProps = {}) {
  const column = (label: string, col: ServiceSortKey, align?: 'right') =>
    sort && onSort ? (
      <SortableHeader label={label} col={col} align={align} sort={sort} onSort={onSort} />
    ) : (
      <TableCell align={align}>{label}</TableCell>
    );
  return (
    <TableHead>
      <TableRow>
        {column('Name', 'name')}
        {column('Status', 'status')}
        {column('Lengths and prices', 'variations', 'right')}
        {column('Members pay with', 'bucket')}
        {column('Who', 'who')}
        {column('Staff approval', 'approval')}
        <TableCell align="right">Actions</TableCell>
        <TableCell sx={{ width: 44 }} aria-hidden />
      </TableRow>
    </TableHead>
  );
}

/**
 * Format a price in the tenant currency for the list cell: whole amounts drop the
 * cents ("$60"), fractional ones keep them ("$62.50"). Plain 2dp until the currency
 * resolves, like the other money cells. Local to this cell; `formatMoney` is unchanged.
 */
function priceLabel(amount: number, currency: string | null): string {
  if (!currency) return amount.toFixed(2);
  if (Number.isInteger(amount)) {
    return new Intl.NumberFormat(undefined, {
      style: 'currency',
      currency,
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    }).format(amount);
  }
  return formatMoney(amount, currency);
}

/**
 * The "Lengths and prices" cell: `3 · $30 to $120` (count, min to max; a single price
 * when min equals max or there is one length), an amber "No lengths yet" for zero, and
 * the "—" fallback when the API did not send a count.
 */
function lengthsAndPrices(service: V2Service, currency: string | null) {
  const count = service.variationCount;
  if (count === undefined) return '—';
  if (count === 0) {
    return <Box component="span" sx={{ color: 'warning.main' }}>No lengths yet</Box>;
  }
  const { minPrice, maxPrice } = service;
  if (minPrice === undefined || maxPrice === undefined) return String(count);
  const price =
    minPrice === maxPrice
      ? priceLabel(minPrice, currency)
      : `${priceLabel(minPrice, currency)} to ${priceLabel(maxPrice, currency)}`;
  return `${count} · ${price}`;
}

export interface ServiceRowProps {
  service: V2Service;
  /** The tenant currency code for the price range (null until it resolves). */
  currency?: string | null;
  /** Resolve a service's credit-bucket label ("Free" / bucket name / raw id). */
  bucketLabel: (service: V2Service) => string;
  onPublish: (service: V2Service) => void;
  onCopy: (service: V2Service) => void;
  onDeactivate: (service: V2Service) => void;
  onReactivate: (service: V2Service) => void;
}

/**
 * One service's `<TableRow>` (the 8 shared columns). The single row definition used by
 * both the flat table and the grouped body — keep all per-row markup/behavior here.
 */
export function ServiceRow({
  service,
  currency = null,
  bucketLabel,
  onPublish,
  onCopy,
  onDeactivate,
  onReactivate,
}: ServiceRowProps) {
  const name = serviceName(service);
  const t = resolveTokens(useTheme());
  const navigate = useNavigate();
  const openDetail = () => navigate(`/services/${service.id}`);
  return (
    <TableRow hover onClick={openDetail} sx={{ cursor: 'pointer' }}>
      <TableCell>
        {/* Featured indicator (DNKADM-0290) — explicit `true` ONLY: `featured` is
            `boolean | undefined`, and absent means the API didn't send the field,
            which is NOT "not featured" (the DNKADM-0256 absent-signal discipline). */}
        {service.featured === true && (
          <Icon
            name="star"
            title="Featured"
            sx={{ mr: 0.5, fontSize: 16, verticalAlign: 'text-bottom', color: t.brand }}
          />
        )}
        <MuiLink
          component={RouterLink}
          to={`/services/${service.id}`}
          onClick={(event) => event.stopPropagation()}
        >
          {name}
        </MuiLink>
      </TableCell>
      <TableCell>
        <ServiceStatusChip status={service.status} />
      </TableCell>
      <TableCell align="right">{lengthsAndPrices(service, currency)}</TableCell>
      <TableCell>{bucketLabel(service)}</TableCell>
      <TableCell>{whoSummary(service)}</TableCell>
      <TableCell>{service.requiresConfirmation ? 'Yes' : 'No'}</TableCell>
      <TableCell align="right">
        <IconAction
          size="small"
          label={`Duplicate service ${name}`}
          onClick={(event) => {
            event.stopPropagation();
            onCopy(service);
          }}
        >
          <Icon name="content_copy" />
        </IconAction>
        {service.status === 'DRAFT' && (
          <IconAction
            size="small"
            label={`Turn on service ${name}`}
            onClick={(event) => {
              event.stopPropagation();
              onPublish(service);
            }}
          >
            <Icon name="publish" />
          </IconAction>
        )}
        {service.status === 'ACTIVE' && (
          <IconAction
            size="small"
            label={`Turn off service ${name}`}
            onClick={(event) => {
              event.stopPropagation();
              onDeactivate(service);
            }}
          >
            <Icon name="toggle_off" />
          </IconAction>
        )}
        {service.status === 'INACTIVE' && (
          <IconAction
            size="small"
            label={`Turn on service ${name}`}
            onClick={(event) => {
              event.stopPropagation();
              onReactivate(service);
            }}
          >
            <Icon name="toggle_on" />
          </IconAction>
        )}
      </TableCell>
      <TableCell align="center" sx={{ color: t.textGhost }}>
        <IconAction
          size="small"
          label={`Open ${name}`}
          onClick={(event) => {
            event.stopPropagation();
            openDetail();
          }}
        >
          <Icon name="chevron_right" />
        </IconAction>
      </TableCell>
    </TableRow>
  );
}

export interface ServicesTableProps extends ServicesTableSortProps {
  services: V2Service[];
  /** The tenant currency code for the price range (null until it resolves). */
  currency?: string | null;
  /** Resolve a service's credit-bucket label ("Free" / bucket name / raw id). */
  bucketLabel: (service: V2Service) => string;
  /** Accessible label for the `<table>` (the flat fallback view). */
  ariaLabel: string;
  onPublish: (service: V2Service) => void;
  onCopy: (service: V2Service) => void;
  onDeactivate: (service: V2Service) => void;
  onReactivate: (service: V2Service) => void;
}

/**
 * The flat (ungrouped) fallback table — a complete `<Table>` (its own head + the shared
 * {@link ServiceRow}s). Used when the inline `categories` field is absent on every row.
 */
export function ServicesTable({
  services,
  currency = null,
  bucketLabel,
  ariaLabel,
  onPublish,
  onCopy,
  onDeactivate,
  onReactivate,
  sort,
  onSort,
}: ServicesTableProps) {
  return (
    <TableContainer>
      <Table aria-label={ariaLabel} size="small">
        <ServicesTableHead sort={sort} onSort={onSort} />
        <TableBody>
          {services.map((service) => (
            <ServiceRow
              key={service.id}
              service={service}
              currency={currency}
              bucketLabel={bucketLabel}
              onPublish={onPublish}
              onCopy={onCopy}
              onDeactivate={onDeactivate}
              onReactivate={onReactivate}
            />
          ))}
        </TableBody>
      </Table>
    </TableContainer>
  );
}
